/**
 * The first-touch cookie `bc_src`.
 *
 * Ninety days, Path=/, SameSite=Lax. On blastoclean.com and its subdomains
 * the Domain is `.blastoclean.com`, so www and app share one cookie. Anywhere
 * else (localhost, a preview host) it stays on that host only — a browser
 * rejects a Domain that is not a suffix of the page.
 *
 * Written once. A later visit does not replace it. A page with no campaign
 * tag and no outside referrer writes nothing, so opening the app directly
 * does not use up the first touch before a campaign link arrives.
 *
 * The JSON uses the same camelCase names the marketing site already stores
 * (`utmSource`, `landingPath`, …) plus `utmTerm` and `utmContent`. A cookie
 * the site wrote before those two existed still parses.
 *
 * `public/bc-attribution.js` is this file for a page that cannot import it.
 * The two must agree; tests/unit/attribution-cookie.test.ts runs both.
 */

import { sanitizeTrafficSource, shrinkTrafficSourceSync, type TrafficSource } from './traffic-source.ts';

export const ATTRIBUTION_COOKIE_NAME = 'bc_src';
export const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
export const ATTRIBUTION_COOKIE_DOMAIN = '.blastoclean.com';
/** Browsers reject a cookie assignment longer than this (RFC 6265). */
export const ATTRIBUTION_COOKIE_LINE_LIMIT = 4096;

const PAYLOAD_KEYS = [
  'referrer',
  'landingPath',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
] as const;

type PayloadKey = (typeof PAYLOAD_KEYS)[number];

export interface PageArrival {
  pathname: string;
  search: string;
  referrer: string;
  hostname: string;
  protocol: string;
}

function bareHost(host: string): string {
  return host.toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');
}

/** blastoclean.com and every subdomain count as one site. www is not special. */
function siteKey(host: string): string {
  const bare = bareHost(host);
  if (bare === 'blastoclean.com' || bare.endsWith('.blastoclean.com')) return 'blastoclean.com';
  return bare.replace(/^www\./, '');
}

/** A referring URL that is not this site. An empty or unparseable referrer is not one. */
export function isExternalReferrer(referrer: string, pageHost: string): boolean {
  if (!referrer || !pageHost) return false;
  try {
    const host = new URL(referrer).hostname;
    if (!host) return false;
    return siteKey(host) !== siteKey(pageHost);
  } catch {
    return false;
  }
}

/**
 * `.blastoclean.com` when this page is on that site, otherwise no Domain
 * attribute. Preview hosts and localhost keep a host-only cookie.
 */
export function attributionCookieDomain(hostname: string): string | null {
  const host = bareHost(hostname);
  if (host === 'blastoclean.com' || host.endsWith('.blastoclean.com')) return ATTRIBUTION_COOKIE_DOMAIN;
  return null;
}

function cookiePair(header: string, name: string): string | null {
  for (const part of header.split(';')) {
    const trimmed = part.trim();
    const index = trimmed.indexOf('=');
    if (index === -1) continue;
    if (trimmed.slice(0, index) !== name) continue;
    return trimmed.slice(index + 1);
  }
  return null;
}

/** True when a non-empty `bc_src` is already there. That value is kept. */
export function attributionCookiePresent(cookieHeader: string | null | undefined): boolean {
  if (!cookieHeader) return false;
  const raw = cookiePair(cookieHeader, ATTRIBUTION_COOKIE_NAME);
  return raw !== null && raw.length > 0;
}

/** The first touch in a Cookie header, or null when it is absent or useless. */
export function readAttributionCookie(cookieHeader: string | null | undefined): TrafficSource | null {
  if (!cookieHeader) return null;
  const raw = cookiePair(cookieHeader, ATTRIBUTION_COOKIE_NAME);
  if (!raw) return null;
  try {
    return sanitizeTrafficSource(JSON.parse(decodeURIComponent(raw)));
  } catch {
    return null;
  }
}

/**
 * What this page load is worth storing, or null when it is not a first touch
 * we want to keep: no campaign tag, and no outside referrer.
 */
export function trafficSourceFromArrival(arrival: PageArrival): TrafficSource | null {
  const params = new URLSearchParams(arrival.search.startsWith('?') ? arrival.search.slice(1) : arrival.search);
  const landing = arrival.pathname || '/';
  const source = sanitizeTrafficSource({
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    referrer: arrival.referrer,
    landing_path: landing,
  });
  if (!source) return null;
  const hasUtm = Boolean(
    source.utmSource || source.utmMedium || source.utmCampaign || source.utmTerm || source.utmContent,
  );
  if (hasUtm) return source;
  if (isExternalReferrer(arrival.referrer, arrival.hostname)) return source;
  return null;
}

function payloadFrom(source: TrafficSource): string {
  const payload: Partial<Record<PayloadKey, string>> = {};
  for (const key of PAYLOAD_KEYS) {
    const value = source[key];
    if (value) payload[key] = value;
  }
  return JSON.stringify(payload);
}

function attributionCookieLine(source: TrafficSource, arrival: PageArrival): string {
  const attributes = [
    `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(payloadFrom(source))}`,
    'Path=/',
    `Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  const domain = attributionCookieDomain(arrival.hostname);
  if (domain) attributes.push(`Domain=${domain}`);
  if (arrival.protocol === 'https:') attributes.push('Secure');
  return attributes.join('; ');
}

/** The `document.cookie` assignment, or null when this visit should not write. */
export function captureFirstTouchCookie(cookieHeader: string | null | undefined, arrival: PageArrival): string | null {
  if (attributionCookiePresent(cookieHeader)) return null;
  const source = trafficSourceFromArrival(arrival);
  if (!source) return null;
  const shrunk = shrinkTrafficSourceSync(source, (candidate) => {
    if (!candidate) return false;
    return attributionCookieLine(candidate, arrival).length <= ATTRIBUTION_COOKIE_LINE_LIMIT;
  });
  if (!shrunk) return null;
  return attributionCookieLine(shrunk, arrival);
}
